import { FlaskConical } from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { V3Button } from "@/components/ui/v3/button";
import { V3Drawer } from "@/components/ui/v3/drawer";
import {
  type PreChartLabs,
  readAccountLabs,
  readVisitLabs,
  usePreChartLabsVersion,
  writeVisitLabs,
} from "@/lib/pre-chart-labs";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth.store";
import { PreChartLabsForm } from "./PreChartLabsForm";

// One line for a choice: "All values", "Abnormal only", "A1c, LDL +2".
export const useLabsLabel = (): ((value: PreChartLabs) => string) => {
  const { t } = useTranslation();
  return (value) => {
    if (value.mode !== "tests") return t(`preChart.labs.${value.mode}`);
    const tests = value.tests ?? [];
    const shown = tests.slice(0, 2).join(", ");
    return tests.length > 2 ? `${shown} +${tests.length - 2}` : shown;
  };
};

/**
 * A pre-chart draft's lab values (clinician feedback 03/11): the chip shows
 * what this pre-chart takes from lab reports (the setting, or its own
 * choice) and opens a sheet to change it for this pre-chart only.
 */
export const PreChartLabsChip = ({
  visitPublicId,
  className,
}: {
  visitPublicId: string;
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  usePreChartLabsVersion();
  const account = useAuthStore((state) => state.userData?.public_id);
  const label = useLabsLabel();
  const setting = readAccountLabs(account);
  const own = readVisitLabs(account, visitPublicId);
  const [open, setOpen] = useState(false);
  // The form starts again from the setting after "Use my setting".
  const [resets, setResets] = useState(0);
  const current = own ?? setting;
  return (
    <>
      <button
        aria-label={t("preChart.labs.chipAria", { value: label(current) })}
        className={cn(
          "inline-flex h-10 min-w-0 max-w-full items-center gap-2 rounded-full bg-v3-panel px-4 text-[14px] font-semibold text-v3-text shadow-v3-hairline-strong transition-colors hover:bg-v3-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring",
          className,
        )}
        data-testid="pre-chart-labs-chip"
        type="button"
        onClick={() => setOpen(true)}
      >
        <FlaskConical aria-hidden className="size-4 shrink-0" />
        <span className="min-w-0 truncate">{label(current)}</span>
      </button>
      <V3Drawer
        footer={
          <V3Button
            className="w-full"
            size="lg"
            variant="primary"
            onClick={() => setOpen(false)}
          >
            {t("common.done")}
          </V3Button>
        }
        open={open}
        testId="pre-chart-labs-sheet"
        title={t("preChart.labs.sheetTitle")}
        onOpenChange={setOpen}
      >
        <div className="space-y-4 px-5 pb-4 pt-1">
          <p className="text-[14px] leading-5 text-v3-text-2">
            {t("preChart.labs.sheetBody")}
          </p>
          <PreChartLabsForm
            key={resets}
            testId="pre-chart-labs-override"
            value={current}
            onChange={(value) => writeVisitLabs(account, visitPublicId, value)}
          />
          {own && (
            <V3Button
              data-testid="pre-chart-labs-use-setting"
              variant="ghost"
              onClick={() => {
                writeVisitLabs(account, visitPublicId, null);
                setResets((n) => n + 1);
              }}
            >
              {t("preChart.labs.useSetting", { value: label(setting) })}
            </V3Button>
          )}
        </div>
      </V3Drawer>
    </>
  );
};
